<template>
  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Bulk Infrastructure Setup</p>
        <h1 class="page-title">Create many new workspaces</h1>
        <p class="page-subtitle">
          Each row creates one complete package: clinic admin account, new organization, new clinic or hospital, and the assignment between them.
        </p>
      </div>
      <div class="hero-actions">
        <button class="ui-button secondary" @click="addRow">
          <i class="fas fa-plus"></i> Add row
        </button>
        <button class="ui-button primary" @click="submitAll" :disabled="isSubmitting || rows.length === 0">
          <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'"></i>
          {{ isSubmitting ? 'Creating...' : 'Create all valid rows' }}
        </button>
      </div>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="stat-label">Rows</span>
        <strong class="stat-value">{{ rows.length }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Ready</span>
        <strong class="stat-value">{{ readyCount }}</strong>
      </div>
      <div class="stat-card">
        <span class="stat-label">Created</span>
        <strong class="stat-value">{{ createdCount }}</strong>
      </div>
    </div>

    <div v-if="pageError" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ pageError }}
    </div>

    <div class="ui-card">
      <div class="ui-card-header">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Setup packages</h2>
            <p class="section-description">
              Account appears first for the operator, but creation runs safely as organization → clinic/hospital → account.
            </p>
          </div>
          <span class="optional-pill">One row = one customer setup</span>
        </div>
      </div>

      <div class="bulk-table-wrap">
        <table class="ui-table bulk-table">
          <thead>
            <tr>
              <th>Admin account</th>
              <th>Integration</th>
              <th>Organization</th>
              <th>Clinic / Hospital</th>
              <th>Location details</th>
              <th>Status</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rows" :key="row.id">
              <td>
                <div class="stack-fields">
                  <input class="ui-input compact" v-model="row.admin_name" placeholder="Admin full name *">
                  <input type="email" class="ui-input compact" v-model="row.admin_email" placeholder="Admin email *" @input="syncUsername(row)">
                  <input class="ui-input compact" v-model="row.username" placeholder="Username *" @input="cleanUsername(row)">
                  <div class="input-with-action">
                    <input :type="row.showPassword ? 'text' : 'password'" class="ui-input compact" v-model="row.password" placeholder="Temporary password *">
                    <button type="button" class="input-action" @click="row.showPassword = !row.showPassword">
                      <i class="fas" :class="row.showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                  </div>
                </div>
              </td>
              <td>
                <label class="integration-toggle">
                  <input type="checkbox" v-model="row.needs_integration">
                  <span class="toggle-track" aria-hidden="true"></span>
                  <span class="toggle-copy">
                    <strong>{{ row.needs_integration ? 'Enabled' : 'Disabled' }}</strong>
                    <small>{{ row.needs_integration ? 'Generate key after account creation' : 'No integration key' }}</small>
                  </span>
                </label>
              </td>
              <td>
                <div class="stack-fields">
                  <input class="ui-input compact" v-model="row.organization_name" placeholder="Organization name *" @input="syncFacilityName(row)">
                  <select class="ui-select compact" v-model="row.organization_type">
                    <option value="clinic">Clinic group</option>
                    <option value="hospital">Hospital group</option>
                  </select>
                  <select class="ui-select compact" v-model="row.subscription_type">
                    <option value="">Subscription not decided</option>
                    <option value="Private">Private</option>
                    <option value="Government">Government</option>
                  </select>
                </div>
              </td>
              <td>
                <div class="stack-fields">
                  <input class="ui-input compact" v-model="row.facility_name" placeholder="Clinic / hospital name *">
                  <select class="ui-select compact" v-model="row.facility_type">
                    <option value="clinic">Clinic</option>
                    <option value="hospital">Hospital</option>
                  </select>
                  <input class="ui-input compact" v-model="row.contact_phone" placeholder="Contact phone" @input="cleanPhone(row)">
                </div>
              </td>
              <td>
                <div class="stack-fields">
                  <input class="ui-input compact" v-model="row.address" placeholder="Address">
                  <div class="inline-fields">
                    <input class="ui-input compact" v-model="row.city" placeholder="City">
                    <select class="ui-select compact" v-model="row.state">
                      <option value="">State</option>
                      <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
                    </select>
                  </div>
                  <input class="ui-input compact" v-model="row.postal_code" placeholder="Postal code">
                </div>
              </td>
              <td>
                <span class="badge-soft" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                <span v-if="row.error" class="row-error">{{ row.error }}</span>
              </td>
              <td class="text-end">
                <div class="row-actions">
                  <button class="icon-button-soft" title="Duplicate row" @click="duplicateRow(row)">
                    <i class="fas fa-copy"></i>
                  </button>
                  <button class="icon-button-soft danger-icon" title="Remove row" @click="removeRow(index)" :disabled="rows.length === 1">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="bulk-footer">
        <div>
          <strong>Required per row:</strong>
          admin name, admin email, username, temporary password, organization name, and clinic/hospital name.
        </div>
        <button class="ui-button secondary" @click="addRow">
          <i class="fas fa-plus"></i> Add another package
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import StatesJSON from "@/constants/states"
import { registerOrg } from "@/services/orgServices"
import { registerClinics } from "@/services/clinicServices"
import { registerUser } from "@/services/userServices"
import { registerIntegration } from "@/services/integrationServices"

const createBlankRow = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  admin_name: "",
  admin_email: "",
  username: "",
  password: "",
  showPassword: false,
  needs_integration: false,
  organization_name: "",
  organization_type: "clinic",
  subscription_type: "Private",
  facility_name: "",
  facility_type: "clinic",
  address: "",
  city: "",
  state: "",
  postal_code: "",
  contact_phone: "",
  status: "draft",
  error: "",
});

export default {
  name: "BulkSetup",
  data() {
    return {
      states: StatesJSON,
      rows: [createBlankRow()],
      isSubmitting: false,
      pageError: "",
    }
  },
  computed: {
    readyCount() {
      return this.rows.filter((row) => this.validateRow(row, { silent: true })).length;
    },
    createdCount() {
      return this.rows.filter((row) => row.status === "created").length;
    },
  },
  methods: {
    addRow() {
      this.rows.push(createBlankRow());
    },
    duplicateRow(row) {
      this.rows.push({
        ...row,
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        status: "draft",
        error: "",
        showPassword: false,
      });
    },
    removeRow(index) {
      if (this.rows.length === 1) {
        return;
      }
      this.rows.splice(index, 1);
    },
    syncUsername(row) {
      if (!row.username && row.admin_email.includes("@")) {
        row.username = row.admin_email.split("@")[0].replace(/\s+/g, "").toLowerCase();
      }
    },
    cleanUsername(row) {
      row.username = row.username.replace(/\s+/g, "").toLowerCase();
    },
    cleanPhone(row) {
      row.contact_phone = row.contact_phone.replace(/\D/g, "");
    },
    syncFacilityName(row) {
      if (!row.facility_name && row.organization_name) {
        row.facility_name = `${row.organization_name} HQ`;
      }
    },
    validateRow(row, { silent = false } = {}) {
      const missing = [];
      if (!row.admin_name.trim()) missing.push("admin name");
      if (!row.admin_email.trim()) missing.push("admin email");
      if (!row.username.trim()) missing.push("username");
      if (!row.password.trim()) missing.push("temporary password");
      if (!row.organization_name.trim()) missing.push("organization name");
      if (!row.facility_name.trim()) missing.push("clinic/hospital name");

      if (missing.length) {
        if (!silent) {
          row.status = "invalid";
          row.error = `Missing ${missing.join(", ")}.`;
        }
        return false;
      }

      if (!silent) {
        row.error = "";
        if (row.status === "invalid") {
          row.status = "draft";
        }
      }
      return true;
    },
    getUuid(response, keys) {
      for (const key of keys) {
        if (response?.[key]) return response[key];
        if (response?.data?.[key]) return response.data[key];
      }
      return "";
    },
    async submitAll() {
      this.pageError = "";
      const validRows = this.rows.filter((row) => this.validateRow(row));

      if (validRows.length === 0) {
        this.pageError = "No valid rows to create yet. Complete at least one setup package first.";
        return;
      }

      this.isSubmitting = true;
      for (const row of validRows) {
        if (row.status === "created") {
          continue;
        }

        row.status = "creating";
        row.error = "";

        try {
          const orgResponse = await registerOrg({
            organization_name: row.organization_name,
            organization_type: row.organization_type,
            hq_address: row.address,
            city: row.city,
            state: row.state,
            country: "Malaysia",
            postal_code: row.postal_code,
            latitude: "",
            longitude: "",
            contact_email: row.admin_email,
            contact_phone: row.contact_phone,
            subscription_plan: "",
            subscription_type: row.subscription_type,
            subscription_status: "",
          });
          const organizationUuid = this.getUuid(orgResponse, ["OrganizationUuid", "organization_uuid", "uuid"]);
          if (!organizationUuid) {
            throw new Error("Organization was created but no organization ID was returned.");
          }

          const clinicResponse = await registerClinics({
            clinic_name: row.facility_name,
            address: row.address,
            city: row.city,
            state: row.state,
            country: "Malaysia",
            postal_code: row.postal_code,
            latitude: "",
            longitude: "",
            OrganizationUuid: organizationUuid,
          });
          const locationUuid = this.getUuid(clinicResponse, ["LocationUuid", "location_uuid", "uuid"]);
          if (!locationUuid) {
            throw new Error("Clinic/hospital was created but no location ID was returned.");
          }

          const userResponse = await registerUser({
            username: row.username,
            email: row.admin_email,
            password: row.password,
            full_name: row.admin_name,
            role: "clinic_admin",
            profession: "Administrator",
            specialization: "Administration",
            clinic_name: row.facility_name,
            contact_number: row.contact_phone,
            locations: [locationUuid],
            additional_info: {},
            OrganizationUuid: organizationUuid,
          });

          if (row.needs_integration) {
            const userUuid = this.getUuid(userResponse, ["UserUuid", "UserUUID", "user_uuid", "uuid"]);
            if (!userUuid) {
              throw new Error("Account was created but no user ID was returned for the integration key.");
            }

            await registerIntegration({
              user_uuid: userUuid,
              description: `Bulk setup integration for ${row.organization_name}`,
            });
          }

          row.status = "created";
        } catch (error) {
          row.status = "failed";
          row.error = error.response?.data?.detail || error.message || "Unable to create this setup package.";
        }
      }
      this.isSubmitting = false;
    },
    statusLabel(status) {
      return {
        draft: "Draft",
        invalid: "Needs info",
        creating: "Creating",
        created: "Created",
        failed: "Failed",
      }[status] || "Draft";
    },
    statusClass(status) {
      return {
        created: "success",
        failed: "danger",
        invalid: "warning",
        creating: "muted",
      }[status] || "muted";
    },
  }
}
</script>

<style scoped>
.hero-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.bulk-table-wrap {
  overflow-x: auto;
  padding: 0 18px 18px;
}

.bulk-table {
  min-width: 1320px;
}

.bulk-table th,
.bulk-table td {
  vertical-align: top;
}

.stack-fields {
  display: grid;
  gap: 8px;
}

.inline-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.integration-toggle {
  display: grid;
  grid-template-columns: 42px minmax(120px, 1fr);
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: var(--ui-text);
}

.integration-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.toggle-track {
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  background: #d9d6e4;
  transition: background 0.18s ease;
}

.toggle-track::after {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(24, 20, 35, 0.2);
  transition: transform 0.18s ease;
}

.integration-toggle input:checked + .toggle-track {
  background: var(--primary-color);
}

.integration-toggle input:checked + .toggle-track::after {
  transform: translateX(18px);
}

.toggle-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.toggle-copy strong {
  font-size: 0.84rem;
  line-height: 1.2;
}

.toggle-copy small {
  color: var(--ui-muted);
  font-size: 0.72rem;
  line-height: 1.25;
}

.ui-input.compact,
.ui-select.compact {
  min-height: 38px;
  border-radius: 10px;
  font-size: 0.84rem;
}

.row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.danger-icon {
  color: var(--error-color);
}

.danger-icon:hover {
  background: var(--error-color);
}

.row-error {
  display: block;
  max-width: 220px;
  margin-top: 8px;
  color: var(--error-color);
  font-size: 0.76rem;
  line-height: 1.35;
}

.bulk-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border-top: 1px solid var(--ui-border);
  background: #fbf9fd;
  color: var(--ui-muted);
  font-size: 0.86rem;
}

@media (max-width: 900px) {
  .hero-actions,
  .bulk-footer {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
